/**
 * RequireAuth HOC - Protects routes that require authentication
 * Redirects unauthenticated users to login page
 */

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { CircularProgress, Box } from '@mui/material';

interface RequireAuthProps {
    children: React.ReactNode;
    redirectTo?: string;
}

/**
 * Higher-Order Component that requires authentication
 * 
 * @example
 * <RequireAuth>
 *   <ProtectedPage />
 * </RequireAuth>
 */
export const RequireAuth: React.FC<RequireAuthProps> = ({ 
    children, 
    redirectTo = '/auth/login' 
}) => {
    const { isAuthenticated, isLoading } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    // Redirect to login if not authenticated (using useEffect to avoid insecure operation error)
    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            navigate(redirectTo, { state: { from: location }, replace: true });
        }
    }, [isAuthenticated, isLoading, navigate, redirectTo, location]);

    // Show loading state while checking authentication
    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    // Don't render children if not authenticated (navigation will happen via useEffect)
    if (!isAuthenticated) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    // User is authenticated, render children
    return <>{children}</>;
};

export default RequireAuth;
